import { RegexConstants } from '@/constants/regex';
import { BuildingNameType } from '@/enums/building';
import i18n from '@/locales';
import type { LocalizedString, PoiContent } from '@/services/dto';

export const DUTY_FREE_TOWN_CODES = ['901', '902', '908', '90G'];
export const DUTY_FREE_STORE_CODES = ['0001', '0002', '0005'];
export const DUTY_FREE_BUILDING_ID = 'duty-free';

/** Extract floor number from names like "8F" / "B1" without importing floor-guide (avoids circular deps). */
const toFloorNumber = (rawFloorName: string): string => {
    if (!rawFloorName) return '';
    const lowerCaseFloorName = rawFloorName.toLowerCase();
    if (RegexConstants.FLOOR_NUMBER_WITH_F_REGEX.test(lowerCaseFloorName)) {
        return lowerCaseFloorName.replace(RegexConstants.FLOOR_NUMBER_WITH_F_REGEX, (_, digits) => digits).trim();
    }
    return rawFloorName;
};

/** storeCode → buildingType → floorNumber → localized floor descriptions */
const DUTY_FREE_FLOOR_DESCRIPTIONS: Record<string, Partial<Record<string, Record<string, LocalizedString>>>> = {
    '0005': {
        [BuildingNameType.MALL]: {
            '8': {
                ko: '(면세점) 명품 · 시계 · 화장품 · 주류 · 식품',
                en: '(Duty Free) Luxury Brands · Watches · Cosmetics · Liquor · Food',
                ja: '(免税店) ラグジュアリーブランド · 腕時計 · 化粧品 · お酒 · 食品',
                'zh-CN': '(免税店) 奢侈品 · 手表 · 化妆品 · 酒类 · 食品',
                'zh-TW': '(免稅店) 奢侈品 · 手錶 · 化妝品 · 酒類 · 食品',
            },
        },
    },
    '0001': {
        [BuildingNameType.MALL]: {
            '10': {
                ko: '(면세점) 명품 · 주얼리 · 시계 · 아이웨어',
                en: '(Duty Free) Luxury Brands · Jewelry · Watches · Eyewear',
                ja: '(免税店) ラグジュアリーブランド · ジュエリー · 腕時計 · アイウェア',
                'zh-CN': '(免税店) 奢侈品 · 珠宝 · 手表 · 眼镜',
                'zh-TW': '(免稅店) 奢侈品 · 珠寶 · 手錶 · 眼鏡',
            },
            '11': {
                ko: '(면세점) 명품 · 주얼리 · 시계 · 패션 · 화장품 · 향수',
                en: '(Duty Free) Luxury Brands · Jewelry · Watches · Fashion · Cosmetics · Perfume',
                ja: '(免税店) ラグジュアリーブランド · ジュエリー · 腕時計 · ファッション · 化粧品 · 香水',
                'zh-CN': '(免税店) 奢侈品 · 珠宝 · 手表 · 时尚服饰 · 化妆品 · 香水',
                'zh-TW': '(免稅店) 奢侈品 · 珠寶 · 手錶 · 時尚服飾 · 化妝品 · 香水',
            },
            '12': {
                ko: '(면세점) 화장품 · 향수',
                en: '(Duty Free) Cosmetics · Perfume',
                ja: '(免税店) 化粧品 · 香水',
                'zh-CN': '(免税店) 化妆品 · 香水',
                'zh-TW': '(免稅店) 化妝品 · 香水',
            },
        },
    },
};

export const getPoiTownCd = (poi: PoiContent, lang?: string): string => {
    return (lang && poi.contents?.cstrTownCd?.[lang]) || poi.contents?.cstrTownCd?.ko || '';
};

export const isDutyFreePoi = (poi: PoiContent, lang?: string): boolean => {
    return DUTY_FREE_TOWN_CODES.includes(getPoiTownCd(poi, lang));
};

export const isDutyFreeStoreCode = (storeCode: string | null | undefined): boolean => {
    return !!storeCode && DUTY_FREE_STORE_CODES.includes(storeCode);
};

export const getDutyFreeBuildingName = (lang?: string): string => {
    return i18n.t('shop.duty_free_building', { lng: lang });
};

export const getPoiBuildingName = (poi: PoiContent, lang: string, defaultBuildingName: string): string => {
    if (isDutyFreePoi(poi, lang)) {
        return getDutyFreeBuildingName(lang);
    }
    return defaultBuildingName;
};

export function getDutyFreeFloorDescriptionMap({
    storeCode,
    buildingType,
    floorName,
}: {
    storeCode?: string | null;
    buildingType?: string | null;
    floorName?: string | null;
}): LocalizedString | null {
    if (!storeCode || !buildingType || !floorName) return null;

    const floorNumber = toFloorNumber(floorName);
    const normalizedBuildingType = String(buildingType).toUpperCase();
    return DUTY_FREE_FLOOR_DESCRIPTIONS[storeCode]?.[normalizedBuildingType]?.[floorNumber] ?? null;
}

export function getDutyFreeFloorDescription({
    storeCode,
    buildingType,
    floorName,
    language,
}: {
    storeCode?: string | null;
    buildingType?: string | null;
    floorName?: string | null;
    language: string;
}): string | null {
    const description = getDutyFreeFloorDescriptionMap({ storeCode, buildingType, floorName });
    if (!description) return null;

    return description[language] || description.ko || null;
}
